import { useEffect, useRef } from "react"
import { View, StyleSheet, ViewStyle, Pressable, Dimensions } from "react-native"

import { colors } from "@/theme"

interface ModalProps {
  visible: boolean
  onClose: () => void
  children: React.ReactNode
  style?: ViewStyle
  animationType?: "fade" | "slide" | "none"
  backdropOpacity?: number
}

export const Modal: React.FC<ModalProps> = ({
  visible,
  onClose,
  children,
  style,
  animationType = "fade",
  backdropOpacity = 0.5,
}) => {
  const _modalRef = useRef<HTMLDivElement>(null)
  const { width: screenWidth, height: _screenHeight } = Dimensions.get("window")
  const isMobile = screenWidth < 768

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && visible) {
        onClose()
      }
    }

    if (visible) {
      document.addEventListener("keydown", handleEscape)
      document.body.style.overflow = "hidden"

      // Add CSS animations to document if not already present
      if (!document.querySelector("#modal-animations")) {
        const style = document.createElement("style")
        style.id = "modal-animations"
        style.textContent = `
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { 
              transform: translateY(100px);
              opacity: 0;
            }
            to { 
              transform: translateY(0);
              opacity: 1;
            }
          }
        `
        document.head.appendChild(style)
      }
    }

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = "unset"
    }
  }, [visible, onClose])

  if (!visible) return null

  return (
    <View style={[styles.container, animationType === "fade" && styles.fadeIn]}>
      <Pressable
        style={[styles.backdrop, { backgroundColor: `rgba(0, 0, 0, ${backdropOpacity})` }]}
        onPress={onClose}
      />
      <View
        style={[
          styles.modalContainer,
          isMobile ? styles.modalContainerMobile : styles.modalContainerDesktop,
          style,
          animationType === "slide" && styles.slideIn,
        ]}
      >
        {children}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  backdrop: {
    bottom: 0,
    left: 0,
    position: "absolute" as any,
    right: 0,
    top: 0,
  },
  container: {
    position: "absolute" as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1500, // Between bottom sheet (1000) and footer (2000)
    justifyContent: "center",
    alignItems: "center",
  },
  fadeIn: {
    // Animation removed for React Native Web compatibility
  },
  modalContainer: {
    backgroundColor: colors.surface,
    borderRadius: 20,
    elevation: 8,
    shadowColor: colors.palette.neutral900,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.15,
    shadowRadius: 12,
  },
  modalContainerDesktop: {
    maxHeight: "90vh" as any,
    maxWidth: 500,
    width: "90%",
  },
  modalContainerMobile: {
    maxHeight: "90vh" as any,
    maxWidth: 400,
    width: "90%",
  },
  slideIn: {
    // Animation removed for React Native Web compatibility
  },
})
